import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import SaveIcon from '@mui/icons-material/Save';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import { toast } from 'react-toastify';
import {
  createUser,
  getAvailableUserProducts,
  getUser,
  updateUser,
} from '../../services/usuarios';
import { useAuthSession } from '../../contexts/AuthSessionContext';
import { getOwnerAvailableProducts, getUserProductsByKeys } from './userProducts';
import { EMAIL_RECOVERY_HELP, isValidUserEmail } from '../../utils/userEmail';
import {
  ACCOUNT_CARD_SX,
  ACCOUNT_PAGE_BG,
  AccountHomeButton,
  AccountSettingsButton,
} from '../common/AccountPageNavigation';

const CARD_SX = {
  ...ACCOUNT_CARD_SX,
};

const PRIMARY_BUTTON_SX = {
  minHeight: 44,
  px: 3,
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 700,
  background: 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)',
  boxShadow: '0 10px 24px -8px rgba(8, 145, 178, 0.45)',
  '&:hover': {
    background: 'linear-gradient(135deg, #0e7490 0%, #155e75 100%)',
  },
};

const buildDefaultForm = (allowedProducts) => {
  const base = {
    nome: '',
    username: '',
    password: '',
    atendabot_atendente: false,
  };
  allowedProducts.forEach((product) => {
    base[product.key] = true;
  });
  return base;
};

const buildFormFromUser = (user, allowedProducts) => {
  const base = {
    nome: user?.nome || '',
    username: user?.username || '',
    password: '',
    atendabot_atendente: Boolean(user?.atendabot_atendente),
  };
  allowedProducts.forEach((product) => {
    base[product.key] = Boolean(user?.[product.key]);
  });
  return base;
};

const UserForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const editing = Boolean(id && id !== 'novo');
  const {
    isAuthenticated,
    isHydrating,
    isOwner,
    atendabotAtendente,
    hasProductAccess,
    session,
  } = useAuthSession();
  const isAtendabotManager =
    atendabotAtendente !== true
    && typeof hasProductAccess === 'function'
    && hasProductAccess('atendabot');
  const canAccessForm = isOwner || isAtendabotManager;

  const [existingUser, setExistingUser] = useState(null);
  const [form, setForm] = useState({
    nome: '',
    username: '',
    password: '',
    atendabot_atendente: false,
  });
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [formInitialized, setFormInitialized] = useState(false);
  const [availableProductKeys, setAvailableProductKeys] = useState(null);
  const [loadingProductOptions, setLoadingProductOptions] = useState(true);
  const [productOptionsError, setProductOptionsError] = useState('');

  const allowedProducts = useMemo(
    () => (
      Array.isArray(availableProductKeys)
        ? getUserProductsByKeys(availableProductKeys)
        : getOwnerAvailableProducts(session)
    ),
    [availableProductKeys, session]
  );
  const isAtendabotEnabled = Boolean(form.atendabot);
  const legacyLogin = editing && !isValidUserEmail(existingUser?.username);
  const usernameChanged = !editing || String(form.username || '').trim() !== String(existingUser?.username || '').trim();

  useEffect(() => {
    if (isHydrating) return;

    if (!isAuthenticated) {
      navigate('/', { replace: true });
      return;
    }

    if (!canAccessForm) {
      toast.error('Você não tem permissão para acessar usuários.');
      navigate('/products', { replace: true });
    }
  }, [canAccessForm, isAuthenticated, isHydrating, navigate]);

  useEffect(() => {
    if (isHydrating) return undefined;
    if (!canAccessForm) {
      setLoadingProductOptions(false);
      return undefined;
    }

    let active = true;
    setLoadingProductOptions(true);
    setProductOptionsError('');
    getAvailableUserProducts()
      .then((productKeys) => {
        if (active) setAvailableProductKeys(productKeys);
      })
      .catch((err) => {
        if (!active) return;
        setProductOptionsError(
          err?.response?.data?.error || 'Não foi possível carregar os produtos disponíveis.',
        );
      })
      .finally(() => {
        if (active) setLoadingProductOptions(false);
      });

    return () => {
      active = false;
    };
  }, [canAccessForm, isHydrating]);

  useEffect(() => {
    if (isHydrating || !canAccessForm || !editing) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    getUser(id)
      .then((data) => {
        setExistingUser(data);
      })
      .catch((err) => {
        console.error('Erro ao carregar usuário:', err);
        setError(err?.message || 'Falha ao carregar usuário.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [canAccessForm, editing, id, isHydrating]);

  useEffect(() => {
    if (
      isHydrating
      || loadingProductOptions
      || !Array.isArray(availableProductKeys)
      || !canAccessForm
      || formInitialized
    ) return;

    if (editing) {
      if (!existingUser) return;
      setForm(buildFormFromUser(existingUser, allowedProducts));
      setFormInitialized(true);
      return;
    }

    setForm(buildDefaultForm(allowedProducts));
    setFormInitialized(true);
  }, [
    allowedProducts,
    availableProductKeys,
    canAccessForm,
    editing,
    existingUser,
    formInitialized,
    isHydrating,
    loadingProductOptions,
  ]);

  const handleChange = (field) => (event) => {
    const { value } = event.target;
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleToggle = (event) => {
    const { name, checked } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: checked,
      ...(name === 'atendabot' && !checked ? { atendabot_atendente: false } : {}),
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);

    if (productOptionsError || !Array.isArray(availableProductKeys)) {
      setError('Carregue os produtos disponíveis antes de salvar o usuário.');
      return;
    }

    const trimmedNome = form.nome?.trim();
    if (!trimmedNome) {
      setError('Informe o nome.');
      return;
    }

    const trimmedUsername = form.username?.trim();
    if (!trimmedUsername || (usernameChanged && !isValidUserEmail(trimmedUsername))) {
      setError('Informe um e-mail válido para o login e a recuperação de senha.');
      return;
    }

    const trimmedPassword = form.password ? form.password.trim() : '';
    if (!editing && !trimmedPassword) {
      setError('Informe uma senha para o usuário.');
      return;
    }

    const payload = allowedProducts.reduce(
      (acc, product) => ({
        ...acc,
        [product.key]: Boolean(form[product.key]),
      }),
      { nome: trimmedNome, username: usernameChanged ? trimmedUsername.toLowerCase() : trimmedUsername }
    );

    if (trimmedPassword) {
      payload.password = trimmedPassword;
    }

    payload.atendabot_atendente = Boolean(form.atendabot && form.atendabot_atendente);
    setSaving(true);
    try {
      if (editing) {
        await updateUser(id, payload);
        toast.success('Usuário atualizado com sucesso.');
      } else {
        await createUser(payload);
        toast.success('Usuário criado com sucesso.');
      }
      navigate('/usuarios');
    } catch (err) {
      console.error('Erro ao salvar usuário:', err);
      setError(err?.response?.data?.error || err?.message || 'Erro ao salvar usuário.');
    } finally {
      setSaving(false);
    }
  };

  if (isHydrating || loadingProductOptions) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!canAccessForm) {
    return null;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', background: ACCOUNT_PAGE_BG, py: { xs: 4, md: 6 } }}>
      <AccountSettingsButton />
      <Box sx={{ maxWidth: 1150, mx: 'auto', px: { xs: 2, md: 3 } }}>
        <Stack spacing={3}>
          <AccountHomeButton />
          <Card sx={{ ...CARD_SX, position: 'relative' }}>
            <Box
              sx={{
                position: 'absolute',
                inset: '0 0 auto 0',
                height: 4,
                background: 'linear-gradient(90deg, #06b6d4 0%, #8b5cf6 50%, #f59e0b 100%)',
              }}
            />
            <CardContent sx={{ p: { xs: 2.5, md: 3 }, pt: { xs: 3, md: 3.5 }, '&:last-child': { pb: { xs: 2.5, md: 3 } } }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: '16px',
                    display: 'grid',
                    placeItems: 'center',
                    color: '#0891b2',
                    background: 'linear-gradient(135deg, rgba(8,145,178,.14), rgba(124,58,237,.10))',
                    border: '1px solid rgba(8,145,178,.18)',
                    flexShrink: 0,
                  }}
                >
                  <PersonAddAltOutlinedIcon sx={{ fontSize: 27 }} />
                </Box>
                <Box>
                  <Typography component="h1" variant="h5" fontWeight={700} sx={{ color: '#0f172a' }}>
                    {editing ? 'Editar usuário' : 'Novo usuário'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b', mt: 0.4 }}>
                    Defina o login e os produtos que este usuário poderá acessar.
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Card component="form" name="team-member-editor" autoComplete="off" onSubmit={handleSubmit} sx={CARD_SX}>
            <CardContent sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#0f172a' }}>
                    Dados de acesso
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b', mt: 0.25 }}>
                    O limite é de 10 contas por restaurante, incluindo a conta owner.
                  </Typography>
                </Box>

          {error && (
            <Alert severity="error">
              {error}
            </Alert>
          )}

          {productOptionsError && (
            <Alert severity="error">
              {productOptionsError}
            </Alert>
          )}

          <TextField
            label="Nome"
            id="team-member-display-name"
            name="team-member-display-name"
            autoComplete="off"
            value={form.nome}
            onChange={handleChange('nome')}
            required
          />

          <TextField
            label={legacyLogin ? 'Login' : 'E-mail de acesso'}
            id="team-member-access-address"
            name="team-member-access-address"
            autoComplete="off"
            type={legacyLogin ? 'text' : 'email'}
            value={form.username}
            onChange={handleChange('username')}
            required
            helperText={editing && usernameChanged ? 'Ao salvar, este e-mail será o novo login desta conta.' : undefined}
            inputProps={{ maxLength: 254, autoCapitalize: 'none', spellCheck: false }}
            InputProps={{ endAdornment: (
              <InputAdornment position="end">
                <Tooltip title={EMAIL_RECOVERY_HELP} enterTouchDelay={0} describeChild>
                  <IconButton type="button" size="small" aria-label="Sobre o e-mail e a recuperação de senha">
                    <HelpOutlineOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ) }}
          />

          <TextField
            label="Senha"
            id="team-member-initial-secret"
            name="team-member-initial-secret"
            autoComplete="new-password"
            type="password"
            value={form.password || ''}
            onChange={handleChange('password')}
            required={!editing}
            helperText={editing ? 'Preencha apenas se deseja alterar a senha.' : ''}
          />

                <Divider />
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#0f172a' }}>
                    Produtos disponíveis
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b', mt: 0.25 }}>
                    São exibidos somente os produtos públicos liberados na conta do owner ou no trial.
                  </Typography>
                </Box>

          {allowedProducts.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Nenhum acesso adicional disponível para o seu usuário.
            </Typography>
          ) : (
            <Stack spacing={1.25}>
              {allowedProducts.map((product) => (
                <Box
                  key={product.key}
                  sx={{
                    px: 2,
                    py: 0.75,
                    borderRadius: '12px',
                    border: '1px solid rgba(15,23,42,.08)',
                    backgroundColor: Boolean(form[product.key]) ? 'rgba(8,145,178,.05)' : '#fff',
                  }}
                >
                  <FormControlLabel
                    sx={{ m: 0, width: '100%', justifyContent: 'space-between' }}
                    labelPlacement="start"
                    control={(
                      <Switch
                        checked={Boolean(form[product.key])}
                        onChange={handleToggle}
                        name={product.key}
                        sx={{ ml: 2 }}
                      />
                    )}
                    label={product.label}
                  />
                </Box>
              ))}
            </Stack>
          )}

          {isAtendabotEnabled && (
            <FormControl component="fieldset" sx={{ mt: 1 }}>
              <FormLabel component="legend" sx={{ fontWeight: 600 }}>
                Permissão no Atendabot
              </FormLabel>
              <Typography variant="caption" sx={{ color: '#64748b', mt: 0.5 }}>
                Gerentes podem configurar o Atendabot e cadastrar usuários. Atendentes ficam restritos ao atendimento das conversas.
              </Typography>
              <ToggleButtonGroup
                exclusive
                value={form.atendabot_atendente ? 'atendente' : 'gerente'}
                onChange={(_, value) => {
                  if (!value) return;
                  setForm((prev) => ({
                    ...prev,
                    atendabot_atendente: value === 'atendente',
                  }));
                }}
                sx={{ mt: 1, alignSelf: 'flex-start' }}
              >
                <ToggleButton value="gerente" sx={{ px: 3, py: 1.5, textTransform: 'none', fontWeight: 600 }}>
                  Gerente
                </ToggleButton>
                <ToggleButton value="atendente" sx={{ px: 3, py: 1.5, textTransform: 'none', fontWeight: 600 }}>
                  Atendente
                </ToggleButton>
              </ToggleButtonGroup>
            </FormControl>
          )}

          <Divider />

          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button
              type="submit"
              variant="contained"
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
              disabled={saving || Boolean(productOptionsError) || !formInitialized}
              sx={PRIMARY_BUTTON_SX}
            >
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Box>
    </Box>
  );
};

export default UserForm;
